/**
 * Sprint S-1 · #23 · Landing SEO de cada centro (`/centro/:slug`).
 * Sprint D2 · adaptado para perfiles freemium:
 *   · Botón WhatsApp si org.whatsapp
 *   · Modal "Solicitar cita" (lead form)
 *   · CTA "Reclamar perfil" si freemium no reclamado
 *   · Badge "Reclamado" / "RASIC verificado"
 *   · Vista previa (noindex) para perfiles pending_review
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL, fmtEur } from "../lib/api";
import { useOrgSeo, slugifyCity } from "../lib/seo";
import { track } from "../lib/analytics";
import GlobalHeader from "../components/layout/GlobalHeader";
import LeadFormModal from "../components/LeadFormModal";
import { StarRating } from "./components/StarRating";
import {
  MapPin, Phone, Clock, Storefront, ArrowRight, ArrowLeft, CheckCircle, Globe,
  Sparkle, InstagramLogo, FacebookLogo, WhatsappLogo, EnvelopeSimple,
  ShieldCheck, Crown,
} from "@phosphor-icons/react";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function CentroLanding() {
  const { slug } = useParams();
  useOrgSeo(slug);
  const [data, setData] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLead, setShowLead] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    // 1) Intentamos endpoint público clásico (solo published, máxima info)
    pub.get(`/public/org/${slug}`)
      .then(({ data: d }) => {
        setData(d); setIsPreview(false);
        track("profile_view", { orgSlug: slug, orgId: d?.org?.id });
      })
      .catch(async () => {
        // 2) Fallback al endpoint marketplace (acepta pending_review freemium)
        try {
          const { data: d2 } = await pub.get(`/marketplace/centros/${slug}`);
          setData({ org: d2.org, catalog: [] });
          setIsPreview(d2.is_preview);
          track("profile_view", { orgSlug: slug, orgId: d2?.org?.id, metadata: { preview: !!d2.is_preview } });
          // noindex en preview
          if (d2.meta_robots) {
            let m = document.querySelector('meta[name="robots"]');
            if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
            m.content = d2.meta_robots;
          }
        } catch { setData(null); }
      })
      .finally(() => setLoading(false));
    pub.get(`/seo/related/${slug}?limit=6`).then((r) => setRelated(r.data?.items || [])).catch(() => {});
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-gray-500" data-testid="centro-loading">Cargando…</div>;
  }
  if (!data?.org) {
    return (
      <div className="min-h-screen grid place-items-center text-center p-6" data-testid="centro-404">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Centro no disponible</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">404</h1>
          <Link to="/marketplace" className="text-sm text-[#0077B6] underline mt-4 inline-block">Ver otros centros</Link>
        </div>
      </div>
    );
  }

  const org = data.org;
  const catalog = data.catalog || [];
  const brandColor = (org.brand_color || "#0033A0").trim();
  const heroBg = org.hero_image_url
    ? `linear-gradient(rgba(26,26,26,0.7), rgba(26,26,26,0.9)), url("${org.hero_image_url}")`
    : `linear-gradient(135deg, #1A1A1A 0%, #1A1A1A 55%, ${brandColor}44 100%)`;

  const dayLabels = {
    mon: "Lun", tue: "Mar", wed: "Mié", thu: "Jue", fri: "Vie", sat: "Sáb", sun: "Dom",
  };
  const hours = org.opening_hours_per_day || {};
  const hasHours = Object.keys(hours).length > 0;

  return (
    <div className="min-h-screen bg-white" data-testid={`centro-landing-${slug}`}>
      <GlobalHeader />

      {/* Sticky · Volver al Marketplace — sprint Visual V2 */}
      <div className="bg-[var(--ag-surface)] border-b border-[var(--ag-border)]" data-testid="centro-back-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-11 flex items-center justify-between gap-3 text-xs">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] transition-colors"
            data-testid="centro-back-to-marketplace"
          >
            <ArrowLeft size={14} weight="bold" /> Volver al Marketplace
          </Link>
          {org.city && (
            <Link
              to={`/ciudad/${slugifyCity(org.city)}`}
              className="text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] transition-colors hidden sm:inline-flex items-center gap-1.5"
              data-testid="centro-back-to-city"
            >
              <Storefront size={13} weight="duotone" /> Ver más centros en {org.city}
            </Link>
          )}
        </div>
      </div>

      {/* Hero · Sprint P.1.5 · Bloque E (CP4): jerarquía visual clara con
          el nombre del centro como identidad dominante above-the-fold. El
          eyebrow "AutoGlow Ecosystem" se mantiene como marca pero reducido y
          discreto para no competir con el nombre real del negocio. */}
      <header
        className="relative text-[var(--ag-text)]"
        style={{ background: heroBg, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <h1
            className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[1.05]"
            data-testid="centro-hero-name"
          >
            {org.name}
          </h1>
          <div className="mt-3 text-[10px] sm:text-[11px] uppercase tracking-[0.25em] opacity-70" style={{ color: brandColor }} data-testid="centro-hero-eyebrow">
            <Sparkle size={11} weight="fill" className="inline mr-1" /> AutoGlow Ecosystem
          </div>
          {(org.city || org.address) && (
            <p className="text-[var(--ag-text)]/85 mt-4 text-sm flex items-center gap-1.5" data-testid="centro-hero-location">
              <MapPin size={14} weight="fill" /> {[org.address, org.city].filter(Boolean).join(" · ")}
            </p>
          )}
          {org.description && (
            <p className="text-[var(--ag-text)]/90 mt-4 max-w-2xl text-base leading-relaxed" data-testid="centro-hero-description">{org.description}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            {/* CTA principal: Solicitar cita (siempre disponible para freemium) o Reservar (paid) */}
            {catalog.length > 0 ? (
              <Link
                to={`/reservar/${slug}`}
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] font-semibold px-5 py-3 rounded-sm shadow-lg transition-colors"
                data-testid="centro-cta-reservar"
              >
                Reservar ahora <ArrowRight size={16} weight="bold" />
              </Link>
            ) : (
              <button
                onClick={() => setShowLead(true)}
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] font-semibold px-5 py-3 rounded-sm shadow-lg transition-colors"
                data-testid="centro-cta-lead"
              >
                <EnvelopeSimple size={16} weight="bold" /> Solicitar cita
              </button>
            )}
            {/* Sprint P.1.5 · Bloque G (CP4): WhatsApp SOLO si `org.whatsapp`
                explícito. NUNCA usar `org.phone` como fallback (generaría un
                enlace roto). */}
            {org.whatsapp && (
              <a
                href={`https://wa.me/${String(org.whatsapp).replace(/\D/g, "")}`}
                target="_blank" rel="noopener"
                onClick={() => track("whatsapp_click", { orgSlug: slug, orgId: org.id })}
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea855] text-[var(--ag-text)] px-5 py-3 rounded-sm transition-colors"
                data-testid="centro-cta-whatsapp"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
            )}
            {org.phone && (
              <a
                href={`tel:${org.phone}`}
                onClick={() => track("phone_click", { orgSlug: slug, orgId: org.id })}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-[var(--ag-text)] px-5 py-3 rounded-sm transition-colors"
                data-testid="centro-cta-phone"
              >
                <Phone size={16} weight="bold" /> {org.phone}
              </a>
            )}
            {/* Sprint P.1.5 · Bloque G · email opcional (solo si existe). */}
            {org.notification_email && (
              <a
                href={`mailto:${org.notification_email}`}
                onClick={() => track("email_click", { orgSlug: slug, orgId: org.id })}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-[var(--ag-text)] px-5 py-3 rounded-sm transition-colors"
                data-testid="centro-cta-email"
              >
                <EnvelopeSimple size={16} weight="bold" /> Email
              </a>
            )}
            {org.website_url && (
              <a
                href={org.website_url} target="_blank" rel="noopener"
                onClick={() => track("website_click", { orgSlug: slug, orgId: org.id })}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-[var(--ag-text)] px-5 py-3 rounded-sm transition-colors"
                data-testid="centro-cta-web"
              >
                <Globe size={16} weight="bold" /> Sitio web
              </a>
            )}
          </div>

          {/* Badges de verificación · Sprint P.1.5 · Bloque M (CP4): usar
              `verification_status` (string enum · autoritativo) NUNCA `is_verified`
              (campo inexistente). Los 1 271 orgs con verification_status="rasic_verified"
              muestran el badge oficial. Ver `lib/verification.js` para el helper canónico. */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {org.verification_status === "rasic_verified" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 px-2.5 py-1 rounded-sm" data-testid="centro-badge-rasic">
                <ShieldCheck size={11} weight="fill" /> RASIC verificado
              </span>
            )}
            {org.is_claimed && org.subscription_status === "trialing" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[var(--ag-gold)]/15 text-amber-300 border border-amber-400/40 px-2.5 py-1 rounded-sm" data-testid="centro-badge-claimed">
                <Crown size={11} weight="fill" /> Reclamado · Trial activo
              </span>
            )}
            {org.is_claimed && org.subscription_status === "active" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 px-2.5 py-1 rounded-sm" data-testid="centro-badge-active">
                <CheckCircle size={11} weight="fill" /> Centro activo
              </span>
            )}
            {isPreview && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-white/10 text-[var(--ag-text)]/70 border border-white/20 px-2.5 py-1 rounded-sm">
                Vista previa · pendiente de revisión
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-12">
        {/* Auditoría SEO · Breadcrumb con enlace a la página de ciudad */}
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap -mt-2" data-testid="centro-breadcrumb">
          <Link to="/marketplace" className="hover:text-[var(--ag-gold)] hover:underline">Marketplace</Link>
          {org.city && (
            <>
              <span aria-hidden>/</span>
              <Link to={`/ciudad/${slugifyCity(org.city)}`} className="hover:text-[var(--ag-gold)] hover:underline" data-testid="centro-breadcrumb-city">{org.city}</Link>
            </>
          )}
          <span aria-hidden>/</span>
          <span className="text-gray-700 font-medium">{org.name}</span>
        </nav>

        {/* Sectores / Especialidades */}
        {org.sectors?.length > 0 && (
          <section data-testid="centro-sectores">
            <h2 className="font-display text-2xl font-bold tracking-tighter mb-4">Especialidades</h2>
            <div className="flex flex-wrap gap-2">
              {org.sectors.map((s) => (
                <span key={s} className="text-xs uppercase tracking-wider bg-gray-100 text-gray-700 px-3 py-1.5 rounded-sm font-semibold">
                  {s}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Catálogo */}
        {catalog.length > 0 && (
          <section data-testid="centro-catalog">
            <h2 className="font-display text-2xl font-bold tracking-tighter mb-4">Servicios y precios</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {catalog.slice(0, 12).map((c) => (
                <div key={c.id} className="border border-gray-200 rounded-md p-4 hover:border-[var(--ag-gold)] transition-colors" data-testid={`centro-service-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base text-[#1A1A1A]">{c.name}</div>
                      {c.description && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{c.description}</div>}
                    </div>
                    <div className="font-display font-black text-xl text-[var(--ag-gold)] tabular-nums shrink-0">
                      {(!c.price && c.price_min == null)
                        ? "Consultar"
                        : (c.requires_inspection || c.price_min != null ? `desde ${fmtEur(c.price_min ?? c.price)}` : fmtEur(c.price))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {catalog.length > 12 && (
              <p className="text-xs text-gray-500 mt-3">Y {catalog.length - 12} servicios más. <Link to={`/reservar/${slug}`} className="text-[#0077B6] underline">Ver todos al reservar →</Link></p>
            )}
          </section>
        )}

        {/* Horarios */}
        {hasHours && (
          <section data-testid="centro-horarios">
            <h2 className="font-display text-2xl font-bold tracking-tighter mb-4 flex items-center gap-2">
              <Clock size={20} weight="duotone" /> Horario
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              {["mon","tue","wed","thu","fri","sat","sun"].map((d) => {
                const v = hours[d];
                const closed = !v || !v.open || !v.close;
                return (
                  <div key={d} className={`px-3 py-2 rounded-sm border ${closed ? "border-gray-200 text-[var(--ag-text-muted)] bg-gray-50" : "border-gray-300 text-gray-800"}`}>
                    <div className="text-[10px] uppercase tracking-wider opacity-70">{dayLabels[d]}</div>
                    <div className="font-mono-tab">{closed ? "Cerrado" : `${v.open}–${v.close}`}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* FAQ — contenido rico para SEO · Sprint P.1.5 Bloque J (CP4):
            primera FAQ pre-abierta para mejorar UX/SEO. Nativos <details>
            renderizan el contenido en el HTML source aunque estén colapsados,
            así que Google puede crawlear las respuestas sin JS. Contenido
            estrictamente factual · multi-sector · nunca inventamos precios,
            horarios ni certificaciones. */}
        <section data-testid="centro-faq">
          <h2 className="font-display text-2xl font-bold tracking-tighter mb-4">Preguntas frecuentes</h2>
          <div className="space-y-3 text-sm">
            <details open className="border border-gray-200 rounded-md p-4">
              <summary className="font-semibold cursor-pointer">¿Cómo reservo en {org.name}?</summary>
              <p className="text-gray-600 mt-2">
                Pulsa «Reservar ahora», elige servicio, fecha y hora, y rellena tus datos. Recibirás confirmación por email al instante. Sin tarjeta, sin compromisos.
              </p>
            </details>
            {org.phone && (
              <details className="border border-gray-200 rounded-md p-4">
                <summary className="font-semibold cursor-pointer">¿Puedo llamar para reservar?</summary>
                <p className="text-gray-600 mt-2">
                  Sí. Puedes llamar al <a href={`tel:${org.phone}`} className="text-[#0077B6] underline">{org.phone}</a> en horario de apertura. La reserva online está disponible 24/7.
                </p>
              </details>
            )}
            {catalog.length > 0 && (
              <details className="border border-gray-200 rounded-md p-4">
                <summary className="font-semibold cursor-pointer">¿Qué servicios ofrece {org.name}?</summary>
                <p className="text-gray-600 mt-2">
                  {catalog.slice(0, 5).map((c) => c.name).join(", ")}
                  {catalog.length > 5 ? `, y ${catalog.length - 5} más.` : "."}
                </p>
              </details>
            )}
            <details className="border border-gray-200 rounded-md p-4">
              <summary className="font-semibold cursor-pointer">¿Cómo me llega la confirmación?</summary>
              <p className="text-gray-600 mt-2">
                Por email, según los datos que indiques al reservar. Incluye fecha, hora, dirección y un enlace para cancelar o modificar la cita.
              </p>
            </details>
            {org.city && (
              <details className="border border-gray-200 rounded-md p-4">
                <summary className="font-semibold cursor-pointer">¿Dónde se encuentra {org.name}?</summary>
                <p className="text-gray-600 mt-2">
                  {[org.address, org.city].filter(Boolean).join(", ")}. Puedes ver más centros en <Link to={`/ciudad/${slugifyCity(org.city)}`} className="text-[#0077B6] underline">{org.city}</Link>.
                </p>
              </details>
            )}
          </div>
          {/* JSON-LD FAQPage schema para rich snippets Google · Sprint P.1.5 Bloque J */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": `¿Cómo reservo en ${org.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `Pulsa "Reservar ahora", elige servicio, fecha y hora, y rellena tus datos. Recibirás confirmación por email al instante.` },
                  },
                  ...(catalog.length > 0 ? [{
                    "@type": "Question",
                    "name": `¿Qué servicios ofrece ${org.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": catalog.slice(0, 5).map((c) => c.name).join(", ") + (catalog.length > 5 ? `, y ${catalog.length - 5} más.` : ".") },
                  }] : []),
                  {
                    "@type": "Question",
                    "name": "¿Cómo me llega la confirmación?",
                    "acceptedAnswer": { "@type": "Answer", "text": "Por email, según los datos que indiques al reservar. Incluye fecha, hora, dirección y un enlace para cancelar o modificar la cita." },
                  },
                ],
              }),
            }}
          />
        </section>

        {/* Internal linking: otros centros */}
        {related.length > 0 && (
          <section data-testid="centro-related">
            <h2 className="font-display text-2xl font-bold tracking-tighter mb-4">
              {org.city ? `Otros centros en ${org.city}` : "Otros centros relacionados"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((o) => (
                <Link
                  key={o.slug}
                  to={`/centro/${o.slug}`}
                  className="border border-gray-200 rounded-md p-4 hover:border-[var(--ag-gold)] hover:shadow-sm transition-all flex items-start gap-3"
                  data-testid={`centro-related-${o.slug}`}
                >
                  {o.logo_url ? (
                    <img src={o.logo_url} alt={o.name} className="w-10 h-10 rounded bg-gray-50 object-contain" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[var(--ag-gold)] text-white grid place-items-center font-bold">
                      {o.name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{o.name}</div>
                    {(o.city || o.locality) && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{[o.city, o.locality].filter(Boolean).join(" · ")}</div>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-[var(--ag-text-muted)] shrink-0 mt-2" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Footer CTA */}
        <section className="border-t border-gray-200 pt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            {org.instagram_url && (
              <a href={org.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--ag-gold)]" aria-label="Instagram">
                <InstagramLogo size={20} weight="duotone" />
              </a>
            )}
            {org.facebook_url && (
              <a href={org.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--ag-gold)]" aria-label="Facebook">
                <FacebookLogo size={20} weight="duotone" />
              </a>
            )}
            {org.website_url && (
              <a href={org.website_url} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--ag-gold)]" aria-label="Web">
                <Globe size={20} weight="duotone" />
              </a>
            )}
            <Link to="/marketplace" className="ml-auto text-[#0077B6] hover:underline inline-flex items-center gap-1">
              <Storefront size={14} /> Ver más centros
            </Link>
          </div>
          <Link
            to={`/reservar/${slug}`}
            className="inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white font-semibold px-5 py-3 rounded-sm transition-colors"
            data-testid="centro-cta-reservar-footer"
          >
            <CheckCircle size={16} weight="fill" /> Reservar en {org.name}
          </Link>
        </section>
        {/* Atribución ODbL para perfiles con datos de OpenStreetMap */}
        {org.source?.type === "osm" && (
          <p className="text-[10px] text-[var(--ag-text-muted)] text-center" data-testid="centro-osm-attribution">
            Datos básicos de este perfil: © Colaboradores de{" "}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">OpenStreetMap</a>{" "}
            (licencia ODbL).
          </p>
        )}

        {/* Banner reclamación — solo si freemium NO reclamado */}
        {org.claimable && (
          <section
            className="border border-amber-300/40 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-md p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4"
            data-testid="centro-claim-banner"
          >
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold flex items-center gap-2">
                <Crown size={13} weight="fill" /> ¿Es tu negocio?
              </div>
              <h3 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[#0a0d12]">
                Reclámalo gratis · 30 días de prueba
              </h3>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                Toma el control del perfil, edita tu información, recibe leads y solicitudes de cita directamente. Sin tarjeta.
              </p>
            </div>
            <Link
              to={`/reclamar/${slug}`}
              className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-black text-white px-5 py-3 rounded-sm text-sm font-semibold tracking-wider uppercase transition-colors whitespace-nowrap"
              data-testid="centro-cta-claim"
            >
              <ShieldCheck size={15} weight="fill" /> Reclamar perfil
            </Link>
          </section>
        )}
      </main>

      {/* Lead modal */}
      {showLead && (
        <LeadFormModal
          slug={slug}
          businessName={org.name}
          onClose={() => setShowLead(false)}
        />
      )}
    </div>
  );
}
